import { DayOfWeek } from "features/Trigger/TemplateTrigger/models";
import { pluralize } from "helpers/string";
import { reminderSchedulingFields } from "./ReminderTrigger/Form/fields";
import { combinedOffsetEventOptions, schedulingFields } from "./TemplateTrigger/Form/fields";
import { TimingDataSchema, TriggerSchema } from "./TemplateTrigger/Form/schema";
import { ReminderTriggerSchema } from "./ReminderTrigger/Form/schema";

// Display Helpers

type FieldOptions = { label: string; value: string }[];

function getFieldLabel(field: any, value: string): string {
  return (field.options as FieldOptions).find((option) => option.value === value)?.label || value;
}

export default function generateSchedulingSummary({
  field,
  trigger,
}: {
  field: string;
  trigger: TriggerSchema | ReminderTriggerSchema;
}): string {
  if (trigger.type === "Triggerable::Dependency") return "";

  const { referenceEvent, offset, offsetUnit } = trigger.data;

  // Derive direction and value from offset
  let offsetDirection: "exact" | "before" | "after" = "exact";
  if (offset > 0) {
    offsetDirection = "after";
  } else if (offset < 0) {
    offsetDirection = "before";
  }
  const offsetValue = Math.abs(offset);

  let dayOfWeek: DayOfWeek | undefined;
  if (offsetUnit === "specific_day") {
    dayOfWeek = (trigger.data as { dayOfWeek: DayOfWeek }).dayOfWeek;
  }

  const fields = field === "remindAtTrigger" ? reminderSchedulingFields(field) : schedulingFields(field);

  if (!referenceEvent) {
    return "Select an event";
  } else if (offsetDirection === "exact") {
    if (referenceEvent === "immediate") {
      const immediateOption = combinedOffsetEventOptions(field).find((option) => option.value.referenceEvent === "immediate");
      return immediateOption?.label ?? getFieldLabel(fields.referenceEvent, referenceEvent);
    }
    return `On ${getFieldLabel(fields.referenceEvent, referenceEvent)}`;
  } else if (offset === 0) {
    return "Select a timing value";
  } else if (!offsetUnit) {
    return "Select a timing unit";
  } else if (offsetUnit === "specific_day") {
    if (!dayOfWeek) return "Select a day of the week";
    const dayLabel = getFieldLabel(fields.dayOfWeek, dayOfWeek);
    return `${offsetValue} ${pluralize(dayLabel, offsetValue)} ${getFieldLabel(
      fields.offsetDirection,
      offsetDirection,
    )} ${getFieldLabel(fields.referenceEvent, referenceEvent)}`;
  }

  // Use pluralize for proper singular/plural
  let unitLabel: string;
  if (offsetUnit === "day") {
    unitLabel = pluralize("Day", offsetValue);
  } else if (offsetUnit === "week") {
    unitLabel = pluralize("Week", offsetValue);
  } else if (offsetUnit === "month") {
    unitLabel = pluralize("Month", offsetValue);
  } else {
    unitLabel = getFieldLabel(fields.offsetUnit, offsetUnit);
  }

  return `${offsetValue} ${unitLabel} ${getFieldLabel(
    fields.offsetDirection,
    offsetDirection,
  )} ${getFieldLabel(fields.referenceEvent, referenceEvent)}`;
}

export function isImmediateNoOffsetTrigger({
  referenceEvent,
  offsetDirection,
}: Pick<TimingDataSchema, "referenceEvent" | "offsetDirection">): boolean {
  return referenceEvent === "immediate" && offsetDirection === "exact";
}
